import React from "react"
import { formatMoney } from "../../../utils/format"

const CHAMAA_LABEL = "Chamaa (Merry-Go-Round)"
const PROJECT_SAVINGS_LABEL = "Project Savings"
const WELFARE_LABEL = "Welfare/Support group"

function valueOrDash(value) {
  if (value === null || value === undefined) return "-"
  if (typeof value === "string" && value.trim() === "") return "-"
  return value
}

function resolveProjectSavingsMonthlyAmount({ perPersonAmount, closingDate }) {
  const amount = Number(perPersonAmount || 0)
  if (!(amount > 0) || !closingDate) return null

  const end = new Date(closingDate)
  if (Number.isNaN(end.getTime())) return null
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  if (end.getTime() <= start.getTime()) return null

  const months = Math.max(
    1,
    Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30))
  )
  return amount / months
}

function moneyOrDash(value) {
  if (value === null || value === undefined || String(value).trim() === "") return "-"
  return formatMoney(Number(value) || 0)
}

function resolveWelfareProbationPreview(form) {
  const value = Number(form?.welfareProbationValue || "")
  const unit = String(form?.welfareProbationUnit || "").toLowerCase()
  const hasProbation = Number.isInteger(value) && value > 0 && ["days", "months"].includes(unit)
  if (!hasProbation) return { label: "-", startDate: "", endDate: "" }

  let startDate = null
  if (form?.welfareEnrollmentEnabled && form?.welfareEnrollmentEndDate) {
    startDate = new Date(`${form.welfareEnrollmentEndDate}T00:00:00`)
  } else if (!form?.welfareEnrollmentEnabled) {
    startDate = new Date()
    startDate.setHours(0, 0, 0, 0)
  }
  if (!startDate || Number.isNaN(startDate.getTime())) {
    return { label: `${value} ${unit}`, startDate: "", endDate: "" }
  }

  const endDate = new Date(startDate)
  if (unit === "days") endDate.setDate(endDate.getDate() + value)
  else endDate.setMonth(endDate.getMonth() + value)

  const toDateLabel = (date) => {
    if (!date || Number.isNaN(date.getTime())) return ""
    return date.toISOString().slice(0, 10)
  }

  return {
    label: `${value} ${unit}`,
    startDate: toDateLabel(startDate),
    endDate: toDateLabel(endDate),
  }
}

function StepReview({ form }) {
  const isOnDemandFrequency = form.invoiceFrequency === "As Needed" || form.invoiceFrequency === "Is Needed"
  const showOtherDescription =
    (form.eventType === "Other" || form.eventType === "Others") &&
    typeof form.otherDescription === "string" &&
    form.otherDescription.trim() !== ""

  const categoryLabel =
    form.eventTypeCategory === "recurring"
      ? "Recurring"
      : form.eventTypeCategory === "non-recurring"
        ? "Non Recurring"
        : "-"
  const recurringSlug = String(form.eventTypeRecurring || "").toLowerCase()
  const isChamaa =
    form.eventTypeRecurring === CHAMAA_LABEL ||
    recurringSlug.includes("chamaa")
  const isProjectSavings =
    form.eventTypeRecurring === PROJECT_SAVINGS_LABEL ||
    recurringSlug.includes("project-savings") ||
    recurringSlug.includes("project savings")
  const isWelfare =
    form.eventTypeRecurring === WELFARE_LABEL ||
    recurringSlug.includes("welfare") ||
    recurringSlug.includes("support-group")
  const isOneTimeEvent = form.eventTypeCategory === "non-recurring"
  const projectSavingsMonthlyAmount = resolveProjectSavingsMonthlyAmount({
    perPersonAmount: form.projectSavingsPerPersonAmount,
    closingDate: form.projectSavingsClosingDate,
  })
  const welfareProbationPreview = resolveWelfareProbationPreview(form)

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Step 3: Review</h3>
        <div className="space-y-1.5 text-xs text-gray-700">
          <p>
            <span className="font-medium">Event:</span>{" "}
            {valueOrDash(form.eventType)}
            {showOtherDescription ? ` (${form.otherDescription})` : ""}
          </p>
          <p>
            <span className="font-medium">Category:</span> {categoryLabel}
          </p>
          <p>
            <span className="font-medium">Group Name:</span> {valueOrDash(form.groupName)}
          </p>

          {isOneTimeEvent && (
            <>
              <p>
                <span className="font-medium">Event Date:</span> {valueOrDash(form.eventDate)}
              </p>
              <p>
                <span className="font-medium">Total Target Amount:</span>{" "}
                {form.totalBudget ? formatMoney(Number(form.totalBudget) || 0) : "-"}
              </p>
              <p>
                <span className="font-medium">Per Share:</span>{" "}
                {form.amountPerPerson ? formatMoney(Number(form.amountPerPerson) || 0) : "-"}
              </p>
              <p>
                <span className="font-medium">Short Event Explainer:</span>{" "}
                {valueOrDash(form.shortEventExplainer)}
              </p>
            </>
          )}

          {form.eventTypeCategory === "recurring" && (
            <>
              {isChamaa ? (
                <>
                  <p>
                    <span className="font-medium">Contribution per Member:</span> {valueOrDash(form.longAmount)}
                  </p>
                  <p>
                    <span className="font-medium">Max Shares Per Person:</span> {valueOrDash(form.chamaaMaxSharesPerMember || "1")}
                  </p>
                  <p>
                    <span className="font-medium">First Cycle Start Date:</span> {valueOrDash(form.chamaaFirstCycleStartDate)}
                  </p>
                  <p>
                    <span className="font-medium">First Cycle Frequency:</span> {valueOrDash(form.chamaaFirstCycleInvoiceFrequency)}
                  </p>
                </>
              ) : isProjectSavings ? (
                <>
                  <p>
                    <span className="font-medium">Per Person Amount:</span>{" "}
                    {form.projectSavingsPerPersonAmount
                      ? formatMoney(Number(form.projectSavingsPerPersonAmount) || 0)
                      : "-"}
                  </p>
                  <p>
                    <span className="font-medium">Closing Date:</span> {valueOrDash(form.projectSavingsClosingDate)}
                  </p>
                  <p>
                    <span className="font-medium">Monthly Amount (Info):</span>{" "}
                    {projectSavingsMonthlyAmount ? formatMoney(projectSavingsMonthlyAmount) : "-"}
                  </p>
                </>
              ) : isWelfare ? (
                <>
                  <p>
                    <span className="font-medium">Registration Fee:</span> {moneyOrDash(form.welfareRegistrationFee)}
                  </p>
                  <p>
                    <span className="font-medium">Enrollment Enabled:</span> {form.welfareEnrollmentEnabled ? "Yes" : "No"}
                  </p>
                  <p>
                    <span className="font-medium">Enrollment Start:</span> {valueOrDash(form.welfareEnrollmentStartDate)}
                  </p>
                  <p>
                    <span className="font-medium">Enrollment End:</span> {valueOrDash(form.welfareEnrollmentEndDate)}
                  </p>
                  <p>
                    <span className="font-medium">Member - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitMember)}
                  </p>
                  <p>
                    <span className="font-medium">Member - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementMember)}
                  </p>
                  <p>
                    <span className="font-medium">Spouse - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitSpouse)}
                  </p>
                  <p>
                    <span className="font-medium">Spouse - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementSpouse)}
                  </p>
                  <p>
                    <span className="font-medium">Parent - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitParent)}
                  </p>
                  <p>
                    <span className="font-medium">Parent - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementParent)}
                  </p>
                  <p>
                    <span className="font-medium">Spouse Parent - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitSpouseParent)}
                  </p>
                  <p>
                    <span className="font-medium">Spouse Parent - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementSpouseParent)}
                  </p>
                  <p>
                    <span className="font-medium">Child - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitChild)}
                  </p>
                  <p>
                    <span className="font-medium">Child - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementChild)}
                  </p>
                  <p>
                    <span className="font-medium">Sibling - Loss Report Amount:</span> {moneyOrDash(form.welfareBenefitSibling)}
                  </p>
                  <p>
                    <span className="font-medium">Sibling - Reimbursement:</span> {moneyOrDash(form.welfareReimbursementSibling)}
                  </p>
                  <p>
                    <span className="font-medium">Probation:</span> {welfareProbationPreview.label}
                  </p>
                  <p>
                    <span className="font-medium">Probation Start:</span>{" "}
                    {welfareProbationPreview.startDate || "-"}
                  </p>
                  <p>
                    <span className="font-medium">Probation End:</span>{" "}
                    {welfareProbationPreview.endDate || "-"}
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <span className="font-medium">Auto Invoicing:</span> {form.autoInvoicing ? "Yes" : "No"}
                  </p>
                  {form.autoInvoicing && (
                    <>
                      <p>
                        <span className="font-medium">Invoice Frequency:</span> {valueOrDash(form.invoiceFrequency)}
                      </p>
                      <p>
                        <span className="font-medium">Start Date:</span> {valueOrDash(form.invoiceStartDate)}
                      </p>
                      {!isOnDemandFrequency && (
                        <p>
                          <span className="font-medium">Set Amount (KSh):</span> {valueOrDash(form.longAmount)}
                        </p>
                      )}
                      <p>
                        <span className="font-medium">Late Payment Fee:</span> {valueOrDash(form.lateFee)}
                      </p>
                      <p>
                        <span className="font-medium">Recipient:</span> {valueOrDash(form.recipient)}
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default StepReview
